import { useState, useMemo } from 'react';
import {
  Container,
  Paper,
  Grid,
  TextField,
  Button,
  Typography,
  Autocomplete,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
  LinearProgress,
  Chip,
  Card,
  CardContent,
  InputAdornment,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import SearchIcon from '@mui/icons-material/Search';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import NatureIcon from '@mui/icons-material/Nature';
import SpeedIcon from '@mui/icons-material/Speed';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import PageLayout from '../common/components/PageLayout';
import ManagementMenu from './ManagementMenu';
import { useTranslation } from '../common/components/LocalizationProvider';
import { useEffectAsync } from '../reactHelper';

const useStyles = makeStyles()((theme) => ({
  container: {
    marginTop: theme.spacing(2),
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
    [theme.breakpoints.down('sm')]: {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
    },
  },
  filterPaper: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  statsCard: {
    height: '100%',
    textAlign: 'center',
  },
  scoreGood: {
    color: theme.palette.success.main,
    fontWeight: 'bold',
    fontSize: '1.5rem',
  },
  scoreWarning: {
    color: theme.palette.warning.main,
    fontWeight: 'bold',
    fontSize: '1.5rem',
  },
  scoreBad: {
    color: theme.palette.error.main,
    fontWeight: 'bold',
    fontSize: '1.5rem',
  },
}));

const EcoDrivingPage = () => {
  const { classes } = useStyles();
  const t = useTranslation();

  const [devices, setDevices] = useState([]);
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [fromDate, setFromDate] = useState(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000));
  const [toDate, setToDate] = useState(new Date());
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [fuelPrice, setFuelPrice] = useState(5.50);

  useEffectAsync(async () => {
    const response = await fetch('/api/devices');
    if (response.ok) {
      setDevices(await response.json());
    }
  }, []);

  const handleSearch = async () => {
    if (selectedDevices.length === 0) return;

    setLoading(true);
    setResults([]);

    try {
      const deviceIds = selectedDevices.map((d) => d.id);
      const from = fromDate.toISOString();
      const to = toDate.toISOString();

      const allResults = [];

      for (const deviceId of deviceIds) {
        const device = devices.find((d) => d.id === deviceId);
        const response = await fetch(`/api/reports/trips?deviceId=${deviceId}&from=${from}&to=${to}`, {
          headers: { Accept: 'application/json' },
        });

        if (response.ok) {
          const trips = await response.json();

          if (trips.length > 0) {
            const avgSpeed = trips.reduce((acc, t) => acc + t.averageSpeed, 0) / trips.length;
            const maxSpeed = Math.max(...trips.map((t) => t.maxSpeed));
            const totalDistance = trips.reduce((acc, t) => acc + t.distance, 0);
            const totalDuration = trips.reduce((acc, t) => acc + t.duration, 0);
            let fuelUsed = trips.reduce((acc, t) => acc + (t.spentFuel || 0), 0);
            let fuelEstimated = false;

            // Se não há dados de combustível do sensor, estimar pelo consumo do dispositivo
            if (fuelUsed === 0 && device?.attributes?.form?.fuelConsumption) {
              const consumptionKmL = parseFloat(device.attributes.form.fuelConsumption);
              if (consumptionKmL > 0) {
                fuelUsed = (totalDistance / 1000) / consumptionKmL;
                fuelEstimated = true;
              }
            }

            const fuelCost = fuelUsed * fuelPrice;

            // Calculate eco score based on driving behavior
            let score = 100;
            // Penalize high average speed
            if (avgSpeed > 80) score -= Math.min(20, (avgSpeed - 80) * 0.5);
            // Penalize very high max speed
            if (maxSpeed > 120) score -= Math.min(30, (maxSpeed - 120) * 0.3);
            // Bonus for fuel efficiency (if data available)
            if (fuelUsed > 0 && totalDistance > 0) {
              const consumption = (fuelUsed / (totalDistance / 1000)) * 100;
              if (consumption < 8) score += 10;
              else if (consumption > 15) score -= 10;
            }

            score = Math.max(0, Math.min(100, Math.round(score)));

            allResults.push({
              deviceId,
              deviceName: device?.name || `Device ${deviceId}`,
              trips: trips.length,
              totalDistance: totalDistance / 1000,
              totalDuration,
              avgSpeed: avgSpeed * 1.852,
              maxSpeed: maxSpeed * 1.852,
              fuelUsed,
              fuelEstimated,
              fuelCost,
              ecoScore: score,
            });
          }
        }
      }

      setResults(allResults);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    if (results.length === 0) return;

    const ExcelJS = (await import('exceljs')).default;
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Eco Driving');

    worksheet.columns = [
      { header: t('deviceTitle'), key: 'deviceName', width: 25 },
      { header: t('reportTrips'), key: 'trips', width: 10 },
      { header: `${t('reportTotalDistance')} (km)`, key: 'totalDistance', width: 18 },
      { header: `${t('reportAverageSpeed')} (km/h)`, key: 'avgSpeed', width: 18 },
      { header: `${t('reportMaximumSpeed')} (km/h)`, key: 'maxSpeed', width: 18 },
      { header: `${t('reportSpentFuel')} (L)`, key: 'fuelUsed', width: 15 },
      { header: `${t('reportFuelCost')} (R$)`, key: 'fuelCost', width: 15 },
      { header: 'Eco Score', key: 'ecoScore', width: 12 },
    ];

    results.forEach((row) => {
      worksheet.addRow({
        deviceName: row.deviceName,
        trips: row.trips,
        totalDistance: row.totalDistance.toFixed(2),
        avgSpeed: row.avgSpeed.toFixed(1),
        maxSpeed: row.maxSpeed.toFixed(1),
        fuelUsed: `${row.fuelUsed.toFixed(2)}${row.fuelEstimated ? ' (est)' : ''}`,
        fuelCost: row.fuelCost.toFixed(2),
        ecoScore: row.ecoScore,
      });
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `eco_driving_${new Date().toISOString().split('T')[0]}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const stats = useMemo(() => {
    if (results.length === 0) return null;
    const avgScore = results.reduce((acc, r) => acc + r.ecoScore, 0) / results.length;
    const totalDistance = results.reduce((acc, r) => acc + r.totalDistance, 0);
    const totalFuel = results.reduce((acc, r) => acc + r.fuelUsed, 0);
    const totalFuelCost = results.reduce((acc, r) => acc + r.fuelCost, 0);
    return {
      avgScore: Math.round(avgScore),
      totalDistance: totalDistance.toFixed(1),
      totalFuel: totalFuel.toFixed(2),
      totalFuelCost: totalFuelCost.toFixed(2),
      vehicleCount: results.length,
    };
  }, [results]);

  const getScoreClass = (score) => {
    if (score >= 80) return classes.scoreGood;
    if (score >= 60) return classes.scoreWarning;
    return classes.scoreBad;
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'success';
    if (score >= 60) return 'warning';
    return 'error';
  };

  return (
    <PageLayout menu={<ManagementMenu />} breadcrumbs={['reportManagement', 'reportEcoDriving']}>
      <Container maxWidth={false} className={classes.container}>
        {/* Filters */}
        <Paper className={classes.filterPaper}>
          <Typography variant="h6" gutterBottom>
            <NatureIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            {t('reportEcoDriving')}
          </Typography>

          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, md: 4 }}>
              <Autocomplete
                multiple
                options={devices}
                getOptionLabel={(option) => option.name || ''}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
                value={selectedDevices}
                onChange={(e, value) => setSelectedDevices(value)}
                renderOption={(props, option) => {
                  const { key, ...rest } = props;
                  return (
                    <li {...rest} key={option.id}>
                      {option.name}
                    </li>
                  );
                }}
                renderInput={(params) => (
                  <TextField {...params} label={t('deviceTitle')} size="small" />
                )}
                limitTags={2}
              />
            </Grid>
            <Grid size={{ xs: 6, md: 2 }}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label={t('reportFrom')}
                  value={fromDate}
                  onChange={setFromDate}
                  slotProps={{ textField: { size: 'small', fullWidth: true } }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid size={{ xs: 6, md: 2 }}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label={t('reportTo')}
                  value={toDate}
                  onChange={setToDate}
                  slotProps={{ textField: { size: 'small', fullWidth: true } }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid size={{ xs: 6, md: 2 }}>
              <TextField
                size="small"
                type="number"
                label={t('reportFuelPrice')}
                value={fuelPrice}
                onChange={(e) => setFuelPrice(Number(e.target.value) || 0)}
                InputProps={{
                  endAdornment: <InputAdornment position="end">R$/L</InputAdornment>,
                }}
                inputProps={{ step: 0.01, min: 0 }}
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 6, md: 2 }}>
              <Button
                variant="contained"
                fullWidth
                startIcon={<SearchIcon />}
                onClick={handleSearch}
                disabled={loading || selectedDevices.length === 0}
              >
                {t('sharedSearch')}
              </Button>
            </Grid>
            <Grid size={{ xs: 6, md: 2 }}>
              <Button
                variant="outlined"
                fullWidth
                startIcon={<FileDownloadIcon />}
                onClick={handleExport}
                disabled={results.length === 0}
              >
                Excel
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {loading && <LinearProgress sx={{ mb: 2 }} />}

        {/* Stats Cards */}
        {stats && (
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid size={{ xs: 6, md: 3 }}>
              <Card className={classes.statsCard}>
                <CardContent>
                  <NatureIcon color="success" sx={{ fontSize: 32 }} />
                  <Typography variant="h6">{t('sharedAverage')} Score</Typography>
                  <Typography className={getScoreClass(stats.avgScore)}>
                    {stats.avgScore}%
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <Card className={classes.statsCard}>
                <CardContent>
                  <SpeedIcon color="primary" sx={{ fontSize: 32 }} />
                  <Typography variant="h6">{t('reportTotalDistance')}</Typography>
                  <Typography variant="h5">{stats.totalDistance} km</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <Card className={classes.statsCard}>
                <CardContent>
                  <LocalGasStationIcon color="warning" sx={{ fontSize: 32 }} />
                  <Typography variant="h6">{t('reportSpentFuel')}</Typography>
                  <Typography variant="h5">{stats.totalFuel} L</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <Card className={classes.statsCard}>
                <CardContent>
                  <AttachMoneyIcon color="success" sx={{ fontSize: 32 }} />
                  <Typography variant="h6">{t('reportTotalFuelCost')}</Typography>
                  <Typography variant="h5">R$ {stats.totalFuelCost}</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Results Table */}
        {results.length > 0 && (
          <Paper>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('deviceTitle')}</TableCell>
                  <TableCell align="right">{t('reportTrips')}</TableCell>
                  <TableCell align="right">{t('reportTotalDistance')} (km)</TableCell>
                  <TableCell align="right">{t('reportAverageSpeed')} (km/h)</TableCell>
                  <TableCell align="right">{t('reportMaximumSpeed')} (km/h)</TableCell>
                  <TableCell align="right">{t('reportSpentFuel')} (L)</TableCell>
                  <TableCell align="right">{t('reportFuelCost')} (R$)</TableCell>
                  <TableCell align="center">Eco Score</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {results
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row) => (
                    <TableRow key={row.deviceId}>
                      <TableCell>{row.deviceName}</TableCell>
                      <TableCell align="right">{row.trips}</TableCell>
                      <TableCell align="right">{row.totalDistance.toFixed(2)}</TableCell>
                      <TableCell align="right">{row.avgSpeed.toFixed(1)}</TableCell>
                      <TableCell align="right">{row.maxSpeed.toFixed(1)}</TableCell>
                      <TableCell align="right">
                        {row.fuelUsed.toFixed(2)}
                        {row.fuelEstimated && <Chip label="est" size="small" sx={{ ml: 0.5 }} />}
                      </TableCell>
                      <TableCell align="right">{row.fuelCost.toFixed(2)}</TableCell>
                      <TableCell align="center">
                        <Chip
                          label={`${row.ecoScore}%`}
                          color={getScoreColor(row.ecoScore)}
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={results.length}
              page={page}
              onPageChange={(e, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </Paper>
        )}

        {!loading && results.length === 0 && selectedDevices.length > 0 && (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <NatureIcon sx={{ fontSize: 48, opacity: 0.5, mb: 2 }} />
            <Typography color="textSecondary">{t('sharedNoData')}</Typography>
          </Paper>
        )}
      </Container>
    </PageLayout>
  );
};

export default EcoDrivingPage;
